// Admin DashboardPage — topbar, 4 KPI stat cards, then a two-column layout:
// left = activity feed + recent payments, right = upcoming sessions +
// certificates ready.
import { Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFetch } from "../../hooks/useApi.js";
import { useSessions } from "../../contexts/SessionsContext.js";
import StatCard from "../../components/shared/StatCard.js";
import ActivityFeedRow, { ActivitySkeletonRow } from "../../components/admin/ActivityFeedRow.js";
import RecentPaymentRow from "../../components/admin/RecentPaymentRow.js";
import UpcomingSessionRow from "../../components/admin/UpcomingSessionRow.js";
import CertificateReadyRow from "../../components/admin/CertificateReadyRow.js";
import { IconActivity, IconCash, IconCalendarEvent, IconCertificate } from "../../components/shared/icons.js";
import { formatKesAbbreviated } from "../../lib/formatMoney.js";
import type { RecentPayment, CertificateReady } from "../../lib/adminDashboardTypes.js";
import type { AdminSnapshot, ActivityLog, PaginatedResult } from "@dm-lms/shared/types";

type ActivityFeedItem = ActivityLog & { actorName: string };

export default function DashboardPage() {
  const { data: snapshot, isLoading: snapshotLoading, error: snapshotError } = useFetch<AdminSnapshot>("/api/admin/snapshot");

  // ---- Left column ----
  const { data: activityData, isLoading: activityLoading } = useFetch<PaginatedResult<ActivityFeedItem>>(
    "/api/admin/activity?page=1&pageSize=10"
  );
  const { data: paymentsData, isLoading: paymentsLoading, error: paymentsError } = useFetch<PaginatedResult<RecentPayment>>(
    "/api/payments?page=1&pageSize=5"
  );

  // ---- Right column ----
  const { sessions: sessionsData, isLoading: sessionsLoading } = useSessions();
  const {
    data: certsData,
    isLoading: certsLoading,
    error: certsError,
  } = useFetch<PaginatedResult<CertificateReady>>("/api/admin/certificates/pending?page=1&pageSize=3");

  const seededCerts = useRef(false);
  const [certsReady, setCertsReady] = useState<CertificateReady[]>([]);
  useEffect(() => {
    if (!certsData || seededCerts.current) return;
    seededCerts.current = true;
    setCertsReady(certsData.items);
  }, [certsData]);

  const upcomingSessions = useMemo(() => {
    const now = Date.now();
    return sessionsData
      .filter((session) => new Date(session.scheduledAt).getTime() > now)
      .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())
      .slice(0, 4);
  }, [sessionsData]);

  return (
    <div className="dm-page">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-dm-navy">Dashboard</h1>
        <button
          type="button"
          disabled
          title="Coming soon"
          className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white opacity-50 disabled:cursor-not-allowed"
        >
          Enroll student
        </button>
      </div>

      {snapshotLoading && <p className="text-dm-slate text-sm">Loading snapshot…</p>}
      {snapshotError && <p className="text-red-600 text-sm">{snapshotError}</p>}

      {snapshot && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total students" value={snapshot.totalStudents} />
          <StatCard label="Active courses" value={snapshot.publishedCourses} />
          <StatCard label="Revenue (KES)" value={formatKesAbbreviated(snapshot.totalRevenueKes)} />
          <StatCard label="Pending payments" value={snapshot.pendingPayments} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT COLUMN */}
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-sm font-medium text-dm-navy">
                <IconActivity width={16} height={16} className="text-dm-slate" />
                Activity feed
              </h2>
              <Link to="/app/admin/activity" className="text-xs font-medium text-dm-blue hover:underline">
                View all
              </Link>
            </div>

            {activityLoading && (
              <div className="divide-y divide-gray-100">
                <ActivitySkeletonRow />
                <ActivitySkeletonRow />
                <ActivitySkeletonRow />
                <ActivitySkeletonRow />
              </div>
            )}

            {!activityLoading && (activityData?.items.length ?? 0) === 0 && (
              <p className="text-sm text-dm-slate">No activity yet.</p>
            )}

            {!activityLoading && (activityData?.items.length ?? 0) > 0 && (
              <div className="divide-y divide-gray-100">
                {activityData!.items.map((item) => (
                  <ActivityFeedRow
                    key={item.id}
                    eventType={item.eventType}
                    description={item.description}
                    createdAt={item.createdAt}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-sm font-medium text-dm-navy">
                <IconCash width={16} height={16} className="text-dm-slate" />
                Recent payments
              </h2>
              <Link to="/app/admin/payments" className="text-xs font-medium text-dm-blue hover:underline">
                View all
              </Link>
            </div>

            {paymentsLoading && <p className="text-sm text-dm-slate">Loading…</p>}
            {paymentsError && <p className="text-sm text-red-600">{paymentsError}</p>}

            {!paymentsLoading && !paymentsError && (paymentsData?.items.length ?? 0) === 0 && (
              <p className="text-sm text-dm-slate">No payments yet.</p>
            )}

            {!paymentsLoading && !paymentsError && (paymentsData?.items.length ?? 0) > 0 && (
              <div className="divide-y divide-gray-100">
                {paymentsData!.items.map((payment) => (
                  <RecentPaymentRow key={payment.id} payment={payment} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-sm font-medium text-dm-navy">
                <IconCalendarEvent width={16} height={16} className="text-dm-slate" />
                Upcoming sessions
              </h2>
              <Link to="/app/admin/sessions" className="text-xs font-medium text-dm-blue hover:underline">
                View all
              </Link>
            </div>

            {sessionsLoading && <p className="text-sm text-dm-slate">Loading…</p>}

            {!sessionsLoading && upcomingSessions.length === 0 && (
              <p className="text-sm text-dm-slate">No upcoming sessions.</p>
            )}

            {!sessionsLoading && upcomingSessions.length > 0 && (
              <div className="divide-y divide-gray-100">
                {upcomingSessions.map((session) => (
                  <UpcomingSessionRow key={session.id} session={session} />
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-sm font-medium text-dm-navy">
                <IconCertificate width={16} height={16} className="text-dm-slate" />
                Certificates ready
              </h2>
              <Link to="/app/admin/certificates" className="text-xs font-medium text-dm-blue hover:underline">
                View all
              </Link>
            </div>

            {certsLoading && <p className="text-sm text-dm-slate">Loading…</p>}
            {certsError && <p className="text-sm text-red-600">{certsError}</p>}

            {!certsLoading && !certsError && certsReady.length === 0 && (
              <p className="text-sm text-dm-slate">No certificates ready.</p>
            )}

            {!certsLoading && !certsError && certsReady.length > 0 && (
              <ul className="divide-y divide-gray-100">
                {certsReady.map((item) => (
                  <CertificateReadyRow
                    key={item.enrollmentId}
                    item={item}
                    onIssued={() =>
                      setCertsReady((prev) => prev.filter((c) => c.enrollmentId !== item.enrollmentId))
                    }
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
